import { useEffect, useMemo, useState } from 'react'
import { fetchMyAvatarBlob } from '../lib/api'
import { useSession } from '../lib/useSession'
import { avatarFgForBg, sanitizeAvatarColor } from '../lib/avatarContrast'

type Props = {
  size?: number
  className?: string
  /** Bump to force refetch after upload/delete. */
  revision?: number
}

function initialsFrom(email: string, name?: string | null): string {
  const n = (name || '').trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
    return n.slice(0, 2).toUpperCase()
  }
  const local = (email || '?').split('@')[0] || '?'
  return local.slice(0, 2).toUpperCase()
}

/** Signed-in user avatar: photo → initials. */
export function UserAvatar({ size = 30, className = '', revision = 0 }: Props) {
  const { user } = useSession()
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let created: string | null = null
    void (async () => {
      if (!user?.has_avatar && revision === 0) {
        // Still try fetch — has_avatar may be stale after upload before session refresh
      }
      const blob = await fetchMyAvatarBlob()
      if (cancelled) {
        if (blob) URL.revokeObjectURL(blob)
        return
      }
      created = blob
      setUrl(blob)
    })()
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [user?.id, user?.has_avatar, revision])

  const initials = useMemo(
    () => initialsFrom(user?.email || '', user?.display_name),
    [user?.email, user?.display_name],
  )
  const bg = sanitizeAvatarColor('#6d28d9', '#6d28d9')
  const fg = avatarFgForBg(bg)

  if (url) {
    return (
      <img
        className={`rd-user-ava ${className}`.trim()}
        src={url}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), objectFit: 'cover' }}
      />
    )
  }

  return (
    <span
      className={`rd-user-ava rd-user-ava-fallback ${className}`.trim()}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.3),
        background: bg,
        color: fg,
        fontSize: Math.max(10, Math.round(size * 0.38)),
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden
    >
      {initials}
    </span>
  )
}
